/**
 * Helper function to convert an image to PNG using canvas
 */
const imageToCanvas = (
	img: HTMLImageElement,
	width?: number,
	height?: number
): HTMLCanvasElement => {
	const canvas = document.createElement( 'canvas' );
	canvas.width = width ?? img.width;
	canvas.height = height ?? img.height;

	const ctx = canvas.getContext( '2d' );
	if ( !ctx ) {
		throw new Error( 'Failed to get canvas context' );
	}

	ctx.drawImage( img, 0, 0 );
	return canvas;
};

/**
 * Helper function to convert canvas to File
 */
const canvasToFile = (
	canvas: HTMLCanvasElement,
	fileName: string,
	quality = 1.0
): Promise<File> => new Promise( ( resolve, reject ) => {
	canvas.toBlob( ( blob ) => {
		if ( !blob ) {
			reject( new Error( 'Failed to convert canvas to blob' ) );
			return;
		}

		const pngFile = new File( [ blob ], fileName, { type: 'image/png' } );
		resolve( pngFile );
	}, 'image/png', quality );
} );

export const tintToWhite = ( file: File ) => new Promise<File>( ( resolve, reject ) => {
	const img = new window.Image();

	img.onload = async () => {
		try {
			const canvas = imageToCanvas( img );
			const ctx = canvas.getContext( '2d' );
			if ( !ctx ) {
				reject( new Error( 'Failed to get canvas context' ) );
				return;
			}

			const imageData = ctx.getImageData( 0, 0, canvas.width, canvas.height );
			const { data } = imageData;

			for ( let i = 0; i < data.length; i += 4 ) {
				if ( data[ i + 3 ] > 0 ) {
					data[ i ] = 255; // R
					data[ i + 1 ] = 255; // G
					data[ i + 2 ] = 255; // B
				}
			}

			ctx.putImageData( imageData, 0, 0 );

			const processedFile = await canvasToFile( canvas, `white_${file.name}` );
			resolve( processedFile );
		} catch ( error ) {
			reject( error );
		}
	};

	img.onerror = () => {
		reject( new Error( 'Failed to load image' ) );
	};

	img.src = URL.createObjectURL( file );
} );

export const convertSVGtoPNG = async (
	file: File,
	{ width = 400, height = 100 }: { width?: number; height?: number } = {}
): Promise<File> => new Promise( ( resolve, reject ) => {
	const reader = new FileReader();

	reader.onload = ( e ) => {
		const img = new Image();
		const svgData = e.target?.result as string;

		img.onload = async () => {
			try {
				const canvas = imageToCanvas( img, img.width || width, img.height || height );
				const fileName = file.name.replace( /\.svg$/i, '.png' );
				const pngFile = await canvasToFile( canvas, fileName );
				resolve( pngFile );
			} catch ( error ) {
				reject( error );
			}
		};

		img.onerror = () => {
			reject( new Error( 'Failed to load SVG image' ) );
		};

		img.src = svgData;
	};

	reader.onerror = () => {
		reject( new Error( 'Failed to read file' ) );
	};

	reader.readAsDataURL( file );
} );

export const isSVGFile = ( file: File ) => file.type === 'image/svg+xml';
